import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { SamplingPlanListResponse } from 'src/app/models/samplingPlan.model';
import { SamplingPlanStatus } from 'src/app/enums/samplingPlanStatus.enum';
import { SamplingPlanService } from 'src/app/services/samplingPlan.service';
import { ToastService } from 'src/app/services/toast.service';
import { CommonService } from 'src/app/services/common.service';
import { AuthService } from 'src/app/services/auth.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { TranslateService } from 'src/app/services/translate.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { riskAssessmentRouteConfig } from 'src/app/shared/constants/riskAssessmentRouteConfig.constants';
import { RoutePaths } from 'src/app/shared/constants/routePaths.constants';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import { ColumnConfig, ChildColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { SamplingPlanDialogComponent } from 'src/app/modals/sampling-plan-dialog/sampling-plan-dialog.component';
import { escapeHtml } from 'src/app/shared/helpers/html-escape.helper';
import { RowActionMenuItem } from 'src/app/shared/grid/core-grid/rowActionMenuItem.model';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirmDialog/confirmDialog.component';
import { DateFormatPipe } from 'src/app/shared/pipes/date-format.pipe';

// DASH-2690 (G11v2): the Planning tab now lists first-class parent Sampling Plans (Name / Site / Year / Status /
// Reference RA / actions) instead of the old per-SEG-per-agent rows. Clicking into a plan opens its detail matrix —
// that page (the Name -> matrix navigation + a coverage column, which needs a new BE field) lands with the
// follow-up stacked PR. This slice is the standard grid view + create/edit.
@Component({
  standalone: false,
    selector: 'app-sample-planning-list',
    templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
    styleUrls: [
        './../../shared/grid/core-grid/core-grid.component.scss',
        './sample-planning-list.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [DateFormatPipe],
})
export class SamplePlanningListComponent extends CoreGridComponent<SamplingPlanListResponse> {
    constructor(
        protected samplingPlanService: SamplingPlanService,
        toastService: ToastService,
        commonService: CommonService,
        dialog: MatDialog,
        authService: AuthService,
        gridViewService: GridViewService,
        gridSettingsService: GridSettingsService,
        private translateService: TranslateService,
        private router: Router,
        private dateFormatPipe: DateFormatPipe,
    ) {
        super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
        this.gridService = samplingPlanService;
        // DASH-2742 (G11v2 approve): per-row Approve / Revert-to-Draft menu (mirrors the plan-detail header actions).
        this.rowActionMenu = (row) => this.buildRowMenuItems(row);
    }

    protected get objectId(): string {
        return RolePermissionObjects.SamplingPlan;
    }

    protected override get isGridViewEnabled(): boolean {
        return true;
    }

    override initializeProperties(): void {
        this.addEntityTitle = 'Sampling Plan';
        this.searchPlaceholder = 'Search Plans';
        this.sortActive = nameOf<SamplingPlanListResponse>(x => x.name);
        this.defaultOrderBy = 'asc';
    }

    override initializeColumns(): void {
        const translate = (text: string) => this.translateService.getDynamicTranslation(text);
        this.columnConfigs = [
            {
                name: nameOf<SamplingPlanListResponse>(x => x.name),
                displayName: 'Name',
                cssClass: () => 'clickable-name',
                headerCssClass: () => 'left-aligned',
                // Rendered via [innerHTML] in core-grid — escape tenant-editable free-text.
                cellTemplate: (row) => escapeHtml(row.name ?? '') || '-',
                onClick: (row) => this.goToPlanDetails(row.id, row.referenceRiskAssessmentName),
            },
            {
                name: nameOf<SamplingPlanListResponse>(x => x.organizationLevelName),
                displayName: 'Site',
                cellTemplate: (row) => escapeHtml(row.organizationLevelName ?? '') || '-',
            },
            {
                name: nameOf<SamplingPlanListResponse>(x => x.year),
                displayName: 'Plan Year',
            },
            new ColumnConfig<SamplingPlanListResponse>({
                name: nameOf<SamplingPlanListResponse>(x => x.status),
                displayName: 'Status',
                cellTemplate: () => '',
                enumFilterMap: this.enumToValueNameArray(SamplingPlanStatus),
                children: (row) => {
                    const statusClass = this.getStatusCssClass(row.status);
                    return [
                        new ChildColumnConfig<SamplingPlanListResponse>({
                            cellTemplate: () => translate(SamplingPlanStatus[row.status]),
                            cssClass: () => `status-badge ${statusClass}`,
                        }),
                    ];
                },
            }),
            {
                name: nameOf<SamplingPlanListResponse>(x => x.referenceRiskAssessmentName),
                displayName: 'Reference Risk Assessment',
                cellTemplate: (row) => escapeHtml(row.referenceRiskAssessmentName ?? '') || '-',
            },
            // DASH-2742 (G11v2 approve): the approval stamp columns. Null-tolerant (a plan that was never approved
            // shows '-'), so existing saved GridViews that predate these columns keep working. The BE DTO already
            // advertises both via ColumnConfig, so they appear in the column-settings flyout / export field config.
            {
                name: nameOf<SamplingPlanListResponse>(x => x.approvedByUserName),
                displayName: 'Approved By',
                cellTemplate: (row) => escapeHtml(row.approvedByUserName ?? '') || '-',
            },
            {
                name: nameOf<SamplingPlanListResponse>(x => x.approvedDate),
                displayName: 'Approved Date',
                cellTemplate: (row) => row.approvedDate ? this.dateFormatPipe.transform(row.approvedDate) : '-',
            },
            { name: 'action', displayName: 'Actions' },
        ];
    }

    // DASH-2742 (G11v2 approve): Approve shows on any not-yet-Approved plan (Draft/Rejected) and is hidden once
    // Approved; Revert to Draft replaces it on an Approved plan so the lock is recoverable (the BE clears the
    // approval stamp on the transition out). Both go through a confirmation step since they lock/unlock the matrix.
    private buildRowMenuItems(row: SamplingPlanListResponse): RowActionMenuItem<SamplingPlanListResponse>[] {
        return [
            {
                label: 'Approve',
                icon: 'check_circle',
                hidden: !this.canUpdate || row.status === SamplingPlanStatus.Approved,
                action: () => this.approvePlan(row),
            },
            {
                label: 'Revert to Draft',
                icon: 'undo',
                hidden: !this.canUpdate || row.status !== SamplingPlanStatus.Approved,
                action: () => this.revertPlan(row),
            },
        ];
    }

    private approvePlan(row: SamplingPlanListResponse): void {
        this.changePlanStatus(
            row,
            SamplingPlanStatus.Approved,
            this.translateService.getDynamicTranslation('Approve Sampling Plan'),
            this.translateService.getDynamicTranslation('Approving this plan locks its matrix against further edits. Continue?'),
            this.translateService.getDynamicTranslation('Approve'),
            this.translateService.getDynamicTranslation('Sampling plan approved'),
        );
    }

    private revertPlan(row: SamplingPlanListResponse): void {
        this.changePlanStatus(
            row,
            SamplingPlanStatus.Draft,
            this.translateService.getDynamicTranslation('Revert Sampling Plan to Draft'),
            this.translateService.getDynamicTranslation('Reverting this plan to Draft unlocks its matrix for editing and clears the approval. Continue?'),
            this.translateService.getDynamicTranslation('Revert to Draft'),
            this.translateService.getDynamicTranslation('Sampling plan reverted to Draft'),
        );
    }

    private changePlanStatus(
        row: SamplingPlanListResponse,
        status: SamplingPlanStatus,
        title: string,
        message: string,
        primaryButtonText: string,
        successMessage: string,
    ): void {
        const data: ConfirmDialogData = {
            title,
            message,
            primaryButtonText,
            primaryCallback: () => this.samplingPlanService.setStatus(row.id, status),
            successMessage,
            secondaryButtonText: this.translateService.getDynamicTranslation('Cancel'),
            errorMessage: this.translateService.getDynamicTranslation('Failed to update the sampling plan status'),
        };
        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data, width: '450px', disableClose: true });
        // Reload the grid only on a confirmed change (the dialog resolves falsy on cancel/error) so the status badge
        // and the Approved By / Approved Date columns refresh.
        dialogRef.afterClosed().subscribe((result: unknown) => {
            if (result) this.loadPage();
        });
    }

    // DASH-2706: mirror risk-assessment goToRiskAssessmentDetails -> open the plan-detail matrix. Pass the pinned
    // Reference RA name along (the matrix DTO only carries the id) so the detail meta-strip can show it; the detail
    // page falls back to a generic "Pinned" label when the name is absent (e.g. direct URL / reload).
    goToPlanDetails(id: string, referenceRiskAssessmentName?: string | null): void {
        // DASH-2718 (item 6): compose the detail URL from the single source of truth (RoutePaths.samplingPlans + id)
        // so the base path lives in one place — goBack() on the detail page navigates to the same constant.
        this.router.navigate([RoutePaths.samplingPlans, id], { state: { referenceRiskAssessmentName } });
    }

    private getStatusCssClass(status: SamplingPlanStatus): string {
        switch (status) {
            case SamplingPlanStatus.Draft:
                return 'status-badge--draft';
            case SamplingPlanStatus.Submitted:
                return 'status-badge--submitted';
            case SamplingPlanStatus.Approved:
                return 'status-badge--approved';
            case SamplingPlanStatus.Rejected:
                return 'status-badge--rejected';
            default:
                return '';
        }
    }

    override initializeOverviewConfigs(): void {
        this.overviewConfigs = [];
    }

    override initializeTabs() {
        return riskAssessmentRouteConfig;
    }

    override getOverviewData(): Observable<void> {
        return of(undefined);
    }

    override openEntityDialog(planId?: string): void {
        // DASH-2706 AC1: open the dialog directly (not via the base openDialog) so a create can land the user in the
        // new plan's detail matrix — the dialog resolves the new plan id on create; edit/cancel keep the grid refresh.
        const isCreate = !planId;
        const dialogRef = this.dialog.open(SamplingPlanDialogComponent, {
            data: { id: planId, isReadonly: false, columnDetails: this.columnDetails } as IFormDialogData,
            width: '760px',
            disableClose: true,
        });
        dialogRef.afterClosed().subscribe((result: unknown) => {
            if (isCreate && typeof result === 'string' && result) {
                this.goToPlanDetails(result);
                return;
            }
            this.loadPage();
        });
    }

    override openEntityDeleteDialog(plan: SamplingPlanListResponse): void {
        this.openDeleteDialog(
            DeleteDialog,
            plan,
            this.samplingPlanService,
            'Sampling Plan',
        );
    }
}
